// Tiny inline-SVG sparkline for KPI cards. No dependency — a single
// polyline + an optional area fill, computed from a plain number[].
//
// Decorative by contract: the surrounding MetricCard already exposes the
// metric's value + delta to assistive tech, so the chart carries
// `aria-hidden="true"` and adds no new semantics. It exists to give the
// reader a shape-of-the-trend at a glance, nothing more.
//
// Drawn in a unit-ish viewBox and stretched via `preserveAspectRatio`
// so one component serves both the hero (taller) and compact (shorter)
// sizes — the caller sets the rendered height with a wrapper class.

interface SparklineProps {
  /** Per-bucket values, oldest → newest. Fewer than 2 points renders
   *  nothing (a single dot isn't a trend). */
  points: number[];
  /** Stroke + fill hue. Defaults to the blurple accent. */
  color?: string;
  className?: string;
}

const VIEW_W = 100;
const VIEW_H = 28;
const PAD_Y = 3;

export function Sparkline({ points, color = "#9583FF", className }: SparklineProps) {
  if (points.length < 2) return null;

  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min;
  const stepX = VIEW_W / (points.length - 1);
  // Flat series (span === 0) pins to the vertical mid-line so it reads as
  // "steady" rather than collapsing to the bottom edge.
  const toY = (v: number) =>
    span === 0 ? VIEW_H / 2 : PAD_Y + (1 - (v - min) / span) * (VIEW_H - PAD_Y * 2);

  const coords = points.map((v, i) => [i * stepX, toY(v)] as const);
  const linePath = coords.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  const areaPath =
    `M ${coords[0][0].toFixed(2)},${VIEW_H} ` +
    coords.map(([x, y]) => `L ${x.toFixed(2)},${y.toFixed(2)}`).join(" ") +
    ` L ${coords[coords.length - 1][0].toFixed(2)},${VIEW_H} Z`;

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
      // ts-chart-reveal: one-shot left→right clip-path wipe matching the
      // section charts. The <svg> is reconciled in place across the card's
      // re-renders, so it plays once on mount (when KPI data resolves), not
      // on every render. Killed under reduced-motion via the CSS rule.
      className={className ? `ts-chart-reveal ${className}` : "ts-chart-reveal"}
    >
      <path d={areaPath} fill={color} fillOpacity={0.1} stroke="none" />
      <polyline
        points={linePath}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
